<!--
    Component Work
    @author Leander Eyer <leander.eyer@elead.ch>
-->

<template>
	<div class="container">
		<div id="content" class="content" v-if="cursor < 0">
			<div class="bar darkText largeText">
				<div class="gridLabel bg-work">{{ model.getDictionary('nav_work')}}</div>

				<input
					type="text"
					class="gridTriple lightInput bg-work-light largeText"
					:placeholder="model.getDictionary('main_search')"
					v-model.trim="searchTerm"
				/>
				<div v-if="searchTerm != ''" class="link work-close" @click="searchTerm = ''">x</div>
			</div>

			<div v-if="list.length == 0" class='work-none'>Leider wurden keine Ergebnisse zu ihrer Suche gefunden</div>
			
			<div class="work-grid" @touchstart="touchStartScroll($event)" @touchend="touchEndScroll($event)" @wheel.prevent="scroll($event.deltaY > 0? 1 : -1)">
				<div
					class="work-image zoomable link"
					v-for="(work, index) in list.slice(slice, slice + sliceSize)"
					:key="work.getID()">
					<img
						v-if="work.getImages().isImage()"
						:src="work.getImages().getImageURL(previewImageQuality)"
						:title="work.getToolTip()"
						@click="select(index + slice)"
					/>
					<div v-else 
						v-html="work.getImages().getMultiMediaHTML(0, 'preview')"
						:title="work.getToolTip()"
						@click="select(index + slice)"></div> 
				</div>
				<div
					class="work-grid-up arrow"
					v-if="slice > 0"
					@click.left="scroll(-1)"
					@mousedown.left="autoscroll('start', -1)"
					@mouseup.left="autoscroll('stop')"
				>&#x25B2;</div>
				<div
					class="work-grid-down arrow"
					v-if="slice < maxSlice"
					@click.left="scroll(1)"
					@mousedown.left="autoscroll('start', 1)"
					@mouseup.left="autoscroll('stop')"
				>&#x25BC;</div>
			</div>
		</div>

		<work-detail :list="list" :index="cursor"></work-detail>
		
	</div>
</template>

<script>

	import Vue from "vue";
	import { GRID_CELLS, GRID_COLUMNS } from "./../model/config.js";
	import { model } from "./../model/Model.js";
	import { processSearchTerm } from "./../util.js";
	import { ListWithCursor } from "./../mixin/ListWithCursor.js";
	import { Scrollable } from "./../mixin/Scrollable.js";
	import WorkDetail from "./WorkDetail.vue";

	export default {
		mixins: [ListWithCursor, Scrollable],
		components: { WorkDetail },

		data: function() {
			return {
				// enable a reference on the model in the template
				model: model,

				// all available elements
				fullList: model.getWorks().getList().filter(function(candidate) {
					return candidate.getFlagHideInArtistList() == false;
				}),
				// search term
				searchTerm: "",

				// source of the preview images
				previewImageQuality: 'small',
				// number of cells visible at once
				sliceSize: GRID_CELLS,
				// scroll velocity
				scrollBy: GRID_COLUMNS
			};
		},

		methods: {
			// filter the list of results
			applyFilter: function() {
				let filter = processSearchTerm(this.searchTerm);
				this.list = this.fullList.filter(function(candidate) {
					return candidate.accept(filter);
				});

				// adjust slice
				this.maxSlice = Math.max(0, this.list.length - this.sliceSize);
				this.slice = Math.min(this.maxSlice, this.slice);
			}
		},

		watch: {
			searchTerm: function() {
				this.applyFilter();
			}
		},

		created: function() {
			this.sliceSize = Vue.prototype.$device == 'large'? GRID_CELLS : 4;
			this.scrollBy =  Vue.prototype.$device == 'large'? GRID_COLUMNS : 2;
			this.previewImageQuality = Vue.prototype.$previewImageQuality;
			this.applyFilter();
		}

	};
</script>

<style>

@media (min-width: 1000px) {

	div.work-close {
		position: absolute;
		right: 3rem;
	}

	div.work-grid {
		position: relative;
		display: flex;
		flex-direction: row;
		flex-wrap: wrap;
		justify-content: flex-start;
	}

	div.work-grid-up {
		position: absolute;
		top: 0;
		right: -1rem;

		cursor: pointer;
		user-select: none;
	}

	div.work-grid-down {
		position: absolute;
		bottom: 2rem;
		right: -1rem;

		cursor: pointer;
		user-select: none;
	}

	div.work-image {
		width: 9rem;
		height: 9rem;
		margin: 0 2rem 2rem 0;
		overflow: hidden;
	}

	div.work-image img, div.work-image div {
		object-fit: cover;
		object-position: center;
		width: 9rem;
		height: 9rem;
	}

}

@media (max-width: 1000px) {

	div.container, div#content, div.work-grid {
		height: 100%;
	}

	div#content {
		position: relative;
		display: flex;
		flex-direction: column;
		flex-wrap: nowrap;
		align-items: stretch;
		align-content: stretch;
	}

	div.work-close {
		position: absolute;
		right: 1rem;
		top: 2.7rem;
	}

	div.work-grid {
		overflow: hidden;

		flex-grow: 1;
		flex-basis: 0;

		position: relative;
		display: flex;
		flex-direction: row;
		flex-wrap: wrap;
		align-items: stretch;
		align-content: stretch;

		margin: 0 -2rem -2rem 0;
	}

	div.work-grid-up {
		font-size: 2rem;
		position: absolute;
		padding-bottom: 3rem;
		padding-left: 3rem;
		top: 0;
		right: -0.5rem;

		cursor: pointer;
		user-select: none;
	}

	div.work-grid-down {
		font-size: 2rem;
		position: absolute;
		padding-top: 3rem;
		padding-left: 3rem;
		bottom: 2rem;
		right: -0.5rem;

		cursor: pointer;
		user-select: none;
	}

	div.work-image {
		flex-grow: 1;
		width: calc(50% - 2rem);
		height: calc(50% - 2rem);
		margin: 0 2rem 2rem 0;
		overflow: hidden;
	}

	div.work-image img, div.work-image div {
		object-fit: cover;
		object-position: center;
		width: 100%;
		height: 100%;
	}

}

</style>